import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ContentFormDialog from '../../components/admin/ContentFormDialog';
import MenuItemFormDialog from '../../components/admin/MenuItemFormDialog';
import { InlineAlert, LoadingState, PageHeader, Toast } from '../../components/admin/ui';

const ContentManagement = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState({ home: null, history: null, contact: null });
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPageKey, setSelectedPageKey] = useState(null);
  const [selectedPageData, setSelectedPageData] = useState(null);
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const fetchAll = useCallback(async () => {
    try {
      const [homeRes, historyRes, contactRes, menuRes] = await Promise.all([
        api.get('/admin/pages/home'),
        api.get('/admin/pages/history'),
        api.get('/admin/pages/contact'),
        api.get('/admin/menu-items'),
      ]);
      setPages({
        home: homeRes.data.data,
        history: historyRes.data.data,
        contact: contactRes.data.data,
      });
      setMenuItems(menuRes.data.data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const toggleMenuVisible = async (item) => {
    setSavingSlug(item.slug);
    try {
      const res = await api.put(`/admin/menu-items/${item.slug}`, {
        visible: !item.visible,
      });
      const updated = res.data.data;
      setMenuItems((prev) => prev.map((m) => (m.slug === updated.slug ? updated : m)));
      setToast({
        open: true,
        message: updated.visible
          ? `«${updated.label}» visible en el menú`
          : `«${updated.label}» oculto del menú`,
        tone: 'success',
      });
    } catch (error) {
      console.error(error);
      setToast({ open: true, message: 'No se pudo actualizar la visibilidad', tone: 'error' });
    } finally {
      setSavingSlug(null);
    }
  };

  if (loading) return <LoadingState />;

  const pageCards = [
    { key: 'home', title: 'Página principal', description: 'Contenido de la página de inicio', data: pages.home },
    { key: 'history', title: 'Historia', description: 'Historia institucional de Team Kitsune', data: pages.history },
    { key: 'contact', title: 'Contacto', description: 'Información de contacto y redes sociales', data: pages.contact },
  ];

  return (
    <div className="admin-page">
      <PageHeader title="Contenido" onBack={() => navigate('/admin')} />
      <p className="mb-6 text-sm text-muted">
        Edita el contenido institucional y controlá qué secciones aparecen en el menú público.
      </p>

      <section className="mb-10">
        <h2 className="mb-2 text-lg font-semibold">Menú, cards de home y visibilidad</h2>
        <p className="mb-4 text-sm text-muted">
          Cada ítem tiene un <strong>slug</strong> fijo. Podés editar el título y el texto de la card
          en Explorar. Si lo ocultás: desaparece del header/footer/móvil, de los atajos de la home y
          no se puede abrir por URL (404).
        </p>
        <InlineAlert tone="info" className="mb-4">
          Login / Panel no forman parte de este menú. El título se usa en el menú y en la card.
        </InlineAlert>
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {menuItems.map((item) => (
            <li key={item.slug} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-ink">{item.label}</p>
                <p className="text-xs text-muted">
                  slug: <code className="rounded bg-canvas px-1">{item.slug}</code>
                  {' · '}
                  path: <code className="rounded bg-canvas px-1">{item.path}</code>
                  {item.show_on_home ? ' · card en home' : ''}
                </p>
                {item.card_description && (
                  <p className="mt-1 line-clamp-2 text-sm text-muted">{item.card_description}</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="btn-admin-secondary !min-h-9"
                  onClick={() => {
                    setSelectedMenuItem(item);
                    setMenuDialogOpen(true);
                  }}
                >
                  Editar
                </button>
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <span className={item.visible ? 'text-success' : 'text-muted'}>
                    {item.visible ? 'Visible' : 'Oculto'}
                  </span>
                  <input
                    type="checkbox"
                    className="h-5 w-5 accent-primary"
                    checked={Boolean(item.visible)}
                    disabled={savingSlug === item.slug}
                    onChange={() => toggleMenuVisible(item)}
                    aria-label={`Visibilidad de ${item.label}`}
                  />
                </label>
              </div>
            </li>
          ))}
          {menuItems.length === 0 && (
            <li className="px-4 py-6 text-sm text-muted">No hay ítems de menú configurados.</li>
          )}
        </ul>
      </section>

      <div className="mb-10 rounded-lg border border-line bg-surface p-5">
        <h2 className="text-lg font-semibold">Tarifas</h2>
        <p className="mt-1 text-sm text-muted">
          Publicá el documento de tarifas como PDF o Markdown con vista previa.
        </p>
        <button
          type="button"
          className="btn-admin-secondary mt-4"
          onClick={() => navigate('/admin/tariffs')}
        >
          Gestionar tarifas
        </button>
      </div>

      <h2 className="mb-4 text-lg font-semibold">Páginas institucionales</h2>
      <ul className="grid gap-4 md:grid-cols-3">
        {pageCards.map((page) => (
          <li key={page.key}>
            <article className="flex h-full flex-col rounded-lg border border-line bg-surface p-5">
              <h3 className="text-lg font-semibold">{page.title}</h3>
              <p className="mt-1 text-sm text-muted">{page.description}</p>
              {page.data && (
                <div className="mt-4 flex-1 text-sm">
                  <p className="font-semibold text-primary">Título actual</p>
                  <p className="text-ink">{page.data.title}</p>
                  <p className="mt-2 font-semibold text-primary">Contenido</p>
                  <p className="line-clamp-3 text-muted">{page.data.content}</p>
                </div>
              )}
              <button
                type="button"
                className="btn-admin-secondary mt-4 w-full"
                onClick={() => {
                  setSelectedPageKey(page.key);
                  setSelectedPageData(page.data);
                  setDialogOpen(true);
                }}
              >
                Editar
              </button>
            </article>
          </li>
        ))}
      </ul>

      <div className="mt-8 rounded-lg border border-line bg-surface p-5">
        <h3 className="font-semibold">Resumen contacto</h3>
        <p className="mt-3 text-sm">
          <strong>Ubicación:</strong> {pages.contact?.location || 'No configurada'}
        </p>
        <p className="mt-1 text-sm">
          <strong>Horarios:</strong>{' '}
          {pages.contact?.schedule_text
            ? `${pages.contact.schedule_title || 'Horarios'} — ${pages.contact.schedule_text}`
            : 'No configurados'}
        </p>
        <p className="mt-1 text-sm">
          <strong>WhatsApp:</strong> {pages.contact?.whatsapp_link || 'No configurado'}
        </p>
        <p className="mt-1 text-sm">
          <strong>Instagram:</strong> {pages.contact?.instagram_link || 'No configurado'}
        </p>
      </div>

      <ContentFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedPageKey(null);
          setSelectedPageData(null);
        }}
        pageKey={selectedPageKey}
        pageData={selectedPageData}
        onSuccess={fetchAll}
      />

      <MenuItemFormDialog
        open={menuDialogOpen}
        item={selectedMenuItem}
        onClose={() => {
          setMenuDialogOpen(false);
          setSelectedMenuItem(null);
        }}
        onSuccess={(updated) => {
          setMenuItems((prev) => prev.map((m) => (m.slug === updated.slug ? updated : m)));
          setToast({ open: true, message: `«${updated.label}» actualizado`, tone: 'success' });
        }}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </div>
  );
};

export default ContentManagement;
